import { useFormik } from "formik";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  getErrorMessage,
  getLastStep,
  isValidPhoneNumberWithRules,
  removeBlobImages,
} from "../../../utilis/common-funtions";
import {
  setApplicantTotal,
  setStepsCompleted,
  setActiveStep,
  setKycVerificationActiveStep,
  updateSignatory,
  deleteSignatory,
  verifyDigilockerStatus,
  setEditPreviewApplicant,
  setShowSaveAndPreview,
  getApplicantDetails
} from "../new-booking-form.slice";
import TextFieldInput from "../../../components/TextFieldInput";
import UploadInput from "../../../components/UploadInput";
import UploadPopup from "../../../shared-components/upload-popup/UploadPopup";
import CommonButton from "../../../shared-components/common-button/CommonButton";
import { useTranslation } from "react-i18next";
import InfoIcon from "@mui/icons-material/Info";
import SelectInput from "../../../components/SelectInput";
import Loader from "../../../components/Loader";
import AddAuthorizedSignatoryButton from "./AddAuthorizedSignatoryButton";
import DeleteIcon from "../../../assets/icons/delete-red.svg";
import DeleteConfirmationModal from "../../../shared-components/delete-confirmation-modal/DeleteConfirmationModal";
import { useState, useEffect, memo } from "react";
import AuthorizedSignatory from "./AuthorizedSignatory";
import { route } from "../../../services/apiRoutes";
import {
  ERROR_MESSAGE,
  PRIMARYDOCUMENT_OPTIONS,
  ADDRESS_PROOF_OPTIONS
} from "../../../config/constant";
import { useHistory, useParams } from "react-router-dom";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import UploadPopupTwoImages from "../../../shared-components/upload-popup/UploadPopupTwoImages";
import { RHFPhoneInput } from "../../../shared-components/phone-input/RHFPhoneInput";
import SkipApplicant from "./SkipApplicant";

function AuthorizedSignatoryDetails({
  applicantNo,
  residentStatus = "",
  relationship = null,
  individualErrors = {},
  isIndividualFlow = false,
}) {
  const { t } = useTranslation();
  const { oppId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    isLoading,
    activeStep,
    kycVerificationActiveStep,
    stepsCompleted,
    applicantTotal,
    masterData,
    applicantData,
    isCorporateFlow,
  } = useSelector((state) => state?.newBookingForm);
  const searchParams = new URLSearchParams(window.location.search);
  const hasStatusFromDigilocker = searchParams.has("status");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState({
    showUploadProfileImage: false,
    showUploadPanImage: false,
    showUploadAadharImage: false,
    showUploadAddressProofImage: false,
  });
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const applicantKey = `applicant${applicantNo}`;
  const currentApplicant = applicantData?.[applicantKey];
  const personalDetails = currentApplicant?.personalDetails;
  const hasAadhaarName = Boolean(personalDetails?.isAadhaarVerified);
  const hasPanName = Boolean(personalDetails?.isPanVerified);
  const hasPanDetails =
    Boolean(personalDetails?.nameAsPerPan) ||
    Boolean(personalDetails?.panNumber) ||
    personalDetails?.isPanSkipped !== undefined;
  const isOCRFlow = !hasAadhaarName && !hasPanName;
  const isAadhaarOcrDone = Boolean(personalDetails?.isAadhaarOcrDone);
  const isPanOcrDone = Boolean(personalDetails?.isPanOcrDone);

  const formik = useFormik({
    initialValues: {
      aadhaarNumber: "",
      aadhaarImage: [],
      isPhysicalAadhaar: false,
      panNumber: "",
      panNumberImage: [],
      isPhysicalPan: false,
      addressProofImage: [],
      isPhysicalAddressProof: false,
      nameAsPerAadhaar: "",
      nameAsPerPan: "",
      primaryDocument: "Aadhaar",
      salutation: "",
      firstName: "",
      lastName: "",
      countryCode: "",
      contactNumber: "",
      emailAddress: "",
      image: [],
      isPhysicalImage: false,
      isAadhaarSkipped: false,
      isPanSkipped: false,
      addressProofType: "",
      isAadhaarVerified: false,
      isPanVerified: false,
    },
    validationSchema: Yup.object({
      aadhaarNumber: Yup.string().when(
        ["residentStatus", "isAadhaarSkipped"],
        {
          is: (residentStatus, isAadhaarSkipped) =>
            residentStatus !== t("residentStatusOptions.PIO_OCI") &&
            !isAadhaarSkipped,
          then: (schema) =>
            schema
              .required(
                t("authorizedSignatoryDetails.fields.aadhaarNumber.validation.required")
              )
              .matches(
               /^[2-9]{1}[0-9]{11}$/,
                t("authorizedSignatoryDetails.fields.aadhaarNumber.validation.pattern")
            )
              .test("last-4-match", function (value) {
                const backendAadhaar = personalDetails?.aadhaarNumber;

                const last4 =
                  backendAadhaar?.toUpperCase().includes("X")
                    ? backendAadhaar.slice(-4)
                    : null;
                if (!last4) return true;
                if (!value || value.length < 4) return true;

                const enteredLast4 = value.slice(-4);

                if (enteredLast4 !== last4) {
                  return this.createError({
                    message: `Last 4 digits must match ${last4}`,
                  });
                }

                return true;
              }

              ),
          otherwise: (schema) => schema.notRequired(),
        }
      ),
      // ✅ PAN number validation (only if not verified)
      panNumber: Yup.string().when("isPanSkipped", {
        is: false,
        then: (schema) =>
          schema
            .matches(
              /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
              t(
                "authorizedSignatoryDetails.fields.panNumber.validation.pattern"
              )
            )
            .required(
              t(
                "authorizedSignatoryDetails.fields.panNumber.validation.required"
              )
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

      aadhaarImage: Yup.array().when(
        ["isPhysicalAadhaar", "isAadhaarSkipped"],
        {
          is: (isPhysicalAadhaar, isAadhaarSkipped) =>
            !isPhysicalAadhaar && !isAadhaarSkipped,
          then: (schema) =>
            schema
              .min(
                1,
                t(
                  "authorizedSignatoryDetails.fields.aadhaarImage.validation.min"
                )
              )
              .required(
                t(
                  "authorizedSignatoryDetails.fields.aadhaarImage.validation.required"
                )
              ),
          otherwise: (schema) => schema.notRequired(),
        }
      ),

      panNumberImage: Yup.array().when(["isPhysicalPan", "isPanSkipped"], {
        is: (isPhysicalPan, isPanSkipped) => !isPhysicalPan && !isPanSkipped,
        then: (schema) =>
          schema
            .min(
              1,
              t(
                "authorizedSignatoryDetails.fields.panNumberImage.validation.min"
              )
            )
            .required(
              t(
                "authorizedSignatoryDetails.fields.panNumberImage.validation.required"
              )
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

      addressProofImage: Yup.array().when(
        ["isPhysicalAddressProof", "addressProofType"],
        {
          is: (isPhysicalAddressProof, addressProofType) =>
            !isPhysicalAddressProof && !!addressProofType,
          then: (schema) =>
            schema
              .min(
                1,
                t(
                  "authorizedSignatoryDetails.fields.addressProofImage.validation.min"
                )
              )
              .required(
                t(
                  "authorizedSignatoryDetails.fields.addressProofImage.validation.required"
                )
              ),
          otherwise: (schema) => schema.notRequired(),
        }
      ),

      primaryDocument: Yup.string()
        .oneOf(
          ["Aadhaar", "PAN"],
          t(
            "authorizedSignatoryDetails.fields.primaryDocument.validation.required"
          )
        )
        .required(
          t(
            "authorizedSignatoryDetails.fields.primaryDocument.validation.required"
          )
        ),

      nameAsPerAadhaar: Yup.string()
        .trim()
        .when("isAadhaarSkipped", {
          is: false,
          then: (schema) =>
            schema.required(
              t(
                "authorizedSignatoryDetails.fields.nameAsPerAadhaar.validation.required"
              )
            ),
          otherwise: (schema) => schema.notRequired(),
        }),

      nameAsPerPan: Yup.string()
        .trim()
        .when("isPanSkipped", {
          is: false,
          then: (schema) =>
            schema.required(
              t(
                "authorizedSignatoryDetails.fields.nameAsPerPan.validation.required"
              )
            ),
          otherwise: (schema) => schema.notRequired(),
        }),

      salutation: Yup.string().required(
        t("authorizedSignatoryDetails.fields.salutation.validation.required")
      ),

      firstName: Yup.string()
        .matches(
          /^[A-Za-z\s]+$/,
          t("authorizedSignatoryDetails.fields.firstName.validation.pattern")
        )
        .min(2, t("authorizedSignatoryDetails.fields.firstName.validation.min"))
        .max(
          50,
          t("authorizedSignatoryDetails.fields.firstName.validation.max")
        )
        .required(
          t("authorizedSignatoryDetails.fields.firstName.validation.required")
        ),

      lastName: Yup.string()
        .matches(
          /^[A-Za-z\s]+$/,
          t("authorizedSignatoryDetails.fields.lastName.validation.pattern")
        )
        .max(50, t("authorizedSignatoryDetails.fields.lastName.validation.max"))
        .nullable()          
        .notRequired(),      


      contactNumber: Yup.string()
        .required(
          t(
            "authorizedSignatoryDetails.fields.contactNumber.validation.required"
          )
        )
        .test(
          "is-valid-phone",
          t(
            "authorizedSignatoryDetails.fields.contactNumber.validation.invalid"
          ),
          (value, context) => {
            const { contactNumber, countryCode } = context.parent;
            return isValidPhoneNumberWithRules(countryCode, contactNumber);
          }
        ),
      emailAddress: Yup.string()
        .matches(
          /^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
          t("authorizedSignatoryDetails.fields.emailAddress.validation.invalid")
        )
        .required(
          t(
            "authorizedSignatoryDetails.fields.emailAddress.validation.required"
          )
        ),

      image: Yup.array().when("isPhysicalImage", {
        is: false,
        then: (schema) =>
          schema
            .min(1, t("authorizedSignatoryDetails.fields.image.validation.min"))
            .required(
              t("authorizedSignatoryDetails.fields.image.validation.required")
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(false);
    },
  });

  useEffect(() => {
    dispatch(getApplicantDetails(`/${oppId}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (personalDetails) {
      formik.setValues({
        aadhaarNumber: personalDetails?.aadhaarNumber || "",
        aadhaarImage: personalDetails?.aadhaarImage || [],
        isPhysicalAadhaar: personalDetails?.isPhysicalAadhaar ?? false,
        panNumber: personalDetails?.panNumber || "",
        panNumberImage: personalDetails?.panImage || [],
        isPhysicalPan: personalDetails?.isPhysicalPan ?? false,
        addressProofImage: personalDetails?.addressProofImage || [],
        isPhysicalAddressProof:
          personalDetails?.isPhysicalAddressProof ?? false,
        nameAsPerAadhaar: personalDetails?.nameAsPerAadhaar || "",
        nameAsPerPan: personalDetails?.nameAsPerPan || "",
        primaryDocument: personalDetails?.primaryDocument
          ? personalDetails?.primaryDocument
          : personalDetails?.isAadhaarSkipped
          ? PRIMARYDOCUMENT_OPTIONS?.Pan
          : PRIMARYDOCUMENT_OPTIONS?.Aadhaar,
        salutation: personalDetails?.salutation || "",
        firstName: personalDetails?.firstName || "",
        lastName: personalDetails?.lastName || "",
        countryCode: personalDetails?.countryCode || "IN",
        contactNumber: personalDetails?.contactNumber || "",
        emailAddress: personalDetails?.emailAddress || "",
        image: personalDetails?.image || [],
        isPhysicalImage: personalDetails?.isPhysicalImage ?? false,
        isAadhaarSkipped: personalDetails?.isAadhaarSkipped ?? false,
        isPanSkipped: personalDetails?.isPanSkipped ?? false,
        addressProofType: personalDetails?.addressProofType || "",
        isAadhaarVerified: personalDetails.isAadhaarVerified,
        isPanVerified: personalDetails.isPanVerified,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData, applicantNo]);

  useEffect(() => {
    checkDigilockerStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStatusFromDigilocker, oppId]);

  const checkDigilockerStatus = async () => {
    try {
      if (hasStatusFromDigilocker) {
        const res = await dispatch(
          verifyDigilockerStatus(`/${oppId}`)
        ).unwrap();
        const url = new URL(window.location);
        url.search = ""; // clears everything after ?
        window.history.replaceState({}, "", url);

        if (res) {
          setSnackbarConfig({
            open: true,
            message: res?.message || ERROR_MESSAGE,
            severity: "success",
          });
        }
      }
    } catch (err) {
      setSnackbarConfig({
        open: true,
        message: getErrorMessage(err),
        severity: "error",
      });
    }
  };

  useEffect(() => {
    if (!currentApplicant) {
      dispatch(setKycVerificationActiveStep(0));
      return;
    }

    if ((hasAadhaarName && hasPanName) || isOCRFlow) {
      dispatch(setKycVerificationActiveStep(1));
    } else if (hasAadhaarName && hasPanDetails) {
      dispatch(setKycVerificationActiveStep(1));
    } else {
      dispatch(setKycVerificationActiveStep(0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentApplicant, hasAadhaarName, hasPanName, isOCRFlow, dispatch]);

  useEffect(() => {
    const splitName = (fullName = "") => {
      if (!fullName) return { firstName: "", lastName: "" };

      const parts = fullName.trim().split(" ");
      const firstName = parts[0] || "";
      let lastName = parts.slice(1).join(" ") || "";

      // ✅ If no last name, set it same as first name
      if (!lastName && firstName) {
        lastName = firstName;
      }

      return { firstName, lastName };
    };

    if (
      formik.values.primaryDocument === PRIMARYDOCUMENT_OPTIONS?.Aadhaar &&
      formik.values.nameAsPerAadhaar
    ) {
      const { firstName, lastName } = splitName(formik.values.nameAsPerAadhaar);
      formik.setFieldValue("firstName", firstName);
      formik.setFieldValue("lastName", lastName);
    }

    if (
      formik.values.primaryDocument === PRIMARYDOCUMENT_OPTIONS?.Pan &&
      formik.values.nameAsPerPan
    ) {
      const { firstName, lastName } = splitName(formik.values.nameAsPerPan);
      formik.setFieldValue("firstName", firstName);
      formik.setFieldValue("lastName", lastName);
    }

    if (formik.values.isAadhaarSkipped && formik.values.nameAsPerPan) {
      const { firstName, lastName } = splitName(formik.values.nameAsPerPan);
      formik.setFieldValue("firstName", firstName);
      formik.setFieldValue("lastName", lastName);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.primaryDocument,
    formik.values.nameAsPerAadhaar,
    formik.values.nameAsPerPan,
    applicantData,
  ]);

  const handleUpdateImageData = (fieldName, imageData) => {
    formik.setFieldValue(fieldName, imageData);
  };

  const handleImageValue = (keyName, imgName) => {
    removeBlobImages();
    formik.setFieldValue(keyName, imgName);
  };

  const handlePhysicalImage = (fieldName, value) => {
    const fieldMap = {
      image: "isPhysicalImage",
      panNumberImage: "isPhysicalPan",
      aadhaarImage: "isPhysicalAadhaar",
      addressProofImage: "isPhysicalAddressProof",
    };

    formik.setFieldValue(fieldMap[fieldName], value);
  };

  const togglePopup = (type) => {
    removeBlobImages();

    const keyMap = {
      profile: "showUploadProfileImage",
      pan: "showUploadPanImage",
      aadhaar: "showUploadAadharImage",
      addressProof: "showUploadAddressProofImage",
    };

    const key = keyMap[type];

    if (key) {
      setShowUploadPopup((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    }
  };
  const buildPayload = (saveForLater = false) => {
    let primaryDocumentValue = formik.values.primaryDocument;

    if (formik.values.isAadhaarSkipped && !formik.values.isPanSkipped) {
      primaryDocumentValue = PRIMARYDOCUMENT_OPTIONS?.Pan;
    } else if (formik.values.isPanSkipped && !formik.values.isAadhaarSkipped) {
      primaryDocumentValue = PRIMARYDOCUMENT_OPTIONS?.Aadhaar;
    } else if (formik.values.isPanSkipped && formik.values.isAadhaarSkipped) {
      primaryDocumentValue = null; // do not send
    }

    return {
      opportunityId: oppId,
      applicantNumber: applicantNo,
      lastStep: getLastStep(applicantData?.lastStep, activeStep),
      saveForLater: saveForLater,
      gstNumber: applicantData?.companyDetails?.gstNumber || "",
      gstApplicant: `Applicant ${applicantNo}`,
      personalDetails: {
        salutation: formik.values.salutation,
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        image: formik.values.image,
        aadhaarNumber: formik.values.aadhaarNumber,
        panNumber: formik.values.panNumber,
        ...(formik?.values?.aadhaarImage?.length
          ? { aadhaarImage: formik.values.aadhaarImage }
          : {}),
        ...(formik?.values?.panNumberImage?.length
          ? { panImage: formik.values.panNumberImage }
          : {}),
        nameAsPerAadhaar: formik.values.nameAsPerAadhaar,
        nameAsPerPan: formik.values.nameAsPerPan,
        primaryDocument: primaryDocumentValue,
        countryCode: formik.values.countryCode,
        contactNumber: formik.values.contactNumber,
        emailAddress: formik.values.emailAddress,
        addressProofType: formik?.values?.addressProofType,
        ...(formik?.values?.addressProofImage?.length
          ? { addressProofImage: formik.values.addressProofImage }
          : {}),
        isPhysicalAadhaar: formik.values?.isPhysicalAadhaar,
        isPhysicalPan: formik.values?.isPhysicalPan,
        isPhysicalAddressProof: formik.values?.isPhysicalAddressProof,
        isPhysicalImage: formik.values.isPhysicalImage,
      },
    };
  };

  const handleSubmit = async (
    addAuthorizedSignatory = false,
    isSaveandPreview = false
  ) => {
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      try {
        const response = await dispatch(
          updateSignatory({
            path: route.UPDATE_SIGNATORY,
            payload: buildPayload(false),
            opportunityId: oppId,
          })
        ).unwrap();

        if (response) {
          let completedSteps = {
            ...stepsCompleted,
            [activeStep]: true,
          };
          dispatch(setStepsCompleted(completedSteps));
          dispatch(setActiveStep(activeStep + 1));

          if (addAuthorizedSignatory) {
            dispatch(setApplicantTotal(applicantNo + 1));
            dispatch(setKycVerificationActiveStep(0));
          }
          dispatch(setShowSaveAndPreview(false));
          if (isSaveandPreview) {
            dispatch(setEditPreviewApplicant(false));
            history.push(`/customer-preview/${oppId}`);
          }
        }
      } catch (error) {
        setSnackbarConfig({
          open: true,
          message: getErrorMessage(error),
          severity: "error",
        });
      }
    } else {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
    }
  };

  const handleAddAuthorizedSignatory = () => {
    handleSubmit(true);
  };

  const handleDeleteAuthorizedSignatory = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteAuthorizedSignatoryCancel = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteAuthorizedSignatoryConfirm = async () => {
    setIsDeleting(true);
    const deleteIndex = activeStep;

    try {
      if (applicantData?.[`applicant${applicantNo}`]) {
        await dispatch(
          deleteSignatory({
            path: route.DELETE_APPLICANT,
            payload: {
              opportunityId: oppId,
              applicantId: applicantNo,
            },
            opportunityId: oppId,
          })
        ).unwrap();
      }
    } catch (error) {
      setSnackbarConfig({
        open: true,
        message: getErrorMessage(error),
        severity: "error",
      });
    } finally {
      const updatedStepsCompleted = Object.keys(stepsCompleted)
        .filter((key) => Number(key) !== deleteIndex)
        .map((key, newIndex) => [newIndex, stepsCompleted[key]])
        .reduce((acc, [newKey, value]) => {
          acc[newKey] = value;
          return acc;
        }, {});

      dispatch(setApplicantTotal(applicantTotal - 1));
      dispatch(setStepsCompleted(updatedStepsCompleted));
      dispatch(setKycVerificationActiveStep(1));
      dispatch(setActiveStep(activeStep - 1));
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handlePrevious = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };
  const handleAddharNameChange = (e) => {
    formik.setFieldValue("nameAsPerAadhaar", e.target.value);
    if (
      formik.values.primaryDocument === PRIMARYDOCUMENT_OPTIONS.Aadhaar &&
      !e.target.value
    ) {
      formik.setFieldValue("firstName", e.target.value);
      formik.setFieldValue("lastName", e.target.value);
    }
  };

  const handlePanNameChange = (e) => {
    formik.setFieldValue("nameAsPerPan", e.target.value);
    if (
      formik.values.primaryDocument === PRIMARYDOCUMENT_OPTIONS.Pan &&
      !e.target.value
    ) {
      formik.setFieldValue("firstName", e.target.value);
      formik.setFieldValue("lastName", e.target.value);
    }
  };
  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onClose={handleDeleteAuthorizedSignatoryCancel}
        onConfirm={handleDeleteAuthorizedSignatoryConfirm}
        title={t("authorizedSignatoryDetails.deleteModal.title")}
        message={t("authorizedSignatoryDetails.deleteModal.message", {
          no: applicantNo,
        })}
        applicantNo={applicantNo}
        loading={isDeleting}
      />
      {!isIndividualFlow && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography
            sx={{
              color: "#1C252E",
              fontSize: "16px",
              fontWeight: "600",
              lineHeight: "36px",
              textAlign: "center",
              flex: 1,
            }}
          >
            {applicantNo === 1
              ? t("authorizedSignatoryDetails.title")
              : `${t("authorizedSignatoryDetails.title")} - ${applicantNo}`}
          </Typography>
          {applicantNo === applicantTotal &&
            applicantTotal > (isCorporateFlow ? 1 : 2) && (
              <Button
                onClick={handleDeleteAuthorizedSignatory}
                startIcon={
                  <img
                    src={DeleteIcon}
                    alt="Delete"
                    style={{ width: 20, height: 20 }}
                  />
                }
                sx={{
                  color: "#d32f2f",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#ffebee",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: { xs: "none", sm: "inline" },
                  }}
                >
                  {t(
                    "authorizedSignatoryDetails.buttons.deleteAuthorizedSignatory"
                  )}
                </Box>
              </Button>
            )}
        </Box>
      )}
      {kycVerificationActiveStep === 0 ? (
        <AuthorizedSignatory
          applicantNo={applicantNo}
          residentStatus={residentStatus}
          individualErrors={individualErrors}
          relationship={relationship}
          isIndividualFlow={isIndividualFlow}
        />
      ) : (
        <>
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <Grid container spacing={2}>
              {/* Aadhaar Upload */}
              {!formik?.values?.isAadhaarSkipped && (
                <Grid item xs={12} sm={6}>
                <UploadInput
                  field={{
                    inputValueFieldName: "aadhaarNumber",
                    label: t("authorizedSignatoryDetails.fields.aadhaarNumber.label"),
                    name: "aadhaarImage",
                    placeholder: formik?.values?.aadhaarNumber,
                    placeholderUpload: formik?.values?.aadhaarNumber,
                    required:
                      formik.values?.residentStatus !==
                      t("residentStatusOptions.PIO_OCI"),
                    editabletextFieldPlaceholder: t(
                      "authorizedSignatoryDetails.fields.aadhaarNumber.placeholder"
                    ),
                    handleOnChange: (e) => {
                      const value = e.target.value?.replace(/\D/g, "");
                      formik.setFieldValue("aadhaarNumber", value?.slice(0, 12));
                    },
                    handleOnClick: () => togglePopup("aadhaar"),
                    physicalCopyValue: formik?.values?.isPhysicalAadhaar,
                  }}
                  textfieldReadonly={false}   
                  showCheckMark={Boolean(personalDetails?.isAadhaarVerified)} // ✅ show green tick
                  formik={formik}
                />
                  {showUploadPopup?.showUploadAadharImage && (
                    <UploadPopupTwoImages
                      heading={t(
                        "authorizedSignatoryDetails.fields.aadhaarImage.popup.heading"
                      )}
                      totalImages={2}
                      closePopup={() => togglePopup("aadhaar")}
                      handleImageValue={handleImageValue}
                      uploadedImageFront={
                        formik?.values?.aadhaarImage?.[0] || []
                      }
                      uploadedImageBack={
                        formik?.values?.aadhaarImage?.[1] || []
                      }
                      fieldName={"aadhaarImage"}
                      handleUpdateImageData={handleUpdateImageData}
                      handlePhysicalImage={handlePhysicalImage}
                      selectedOptionValue={formik?.values?.isPhysicalAadhaar}
                      dynamicSize={2}
                      showPreview={isAadhaarOcrDone ? "show" : ""}
                      showDeleteIcon={!isAadhaarOcrDone}
                    />
                  )}
                </Grid>
              )}

              {/* PAN Upload */}
              {!formik?.values?.isPanSkipped && (
                <Grid item xs={12} sm={6}>
                  <UploadInput
                    field={{
                      inputValueFieldName: "panNumber",
                      label: t(
                        "authorizedSignatoryDetails.fields.panNumber.label"
                      ),
                      name: "panNumberImage",
                      placeholder: formik?.values?.panNumber,
                      placeholderUpload: formik?.values?.panNumber,
                      required: true,
                      handleOnChange: (e) => {
                        const value = e.target?.value?.replace(
                          /[^A-Za-z0-9]/g,
                          ""
                        ); // allow only letters + numbers
                        formik.setFieldValue(
                          "panNumber",
                          value?.toUpperCase()?.slice(0, 10)
                        ); // uppercase + limit
                      },
                      editabletextFieldPlaceholder: t(
                        "authorizedSignatoryDetails.fields.panNumber.placeholder"
                      ),

                      handleOnClick: () => togglePopup("pan"),
                      physicalCopyValue: formik?.values?.isPhysicalPan,
                    }}
                    textfieldReadonly={
                      Boolean(personalDetails?.isPanVerified)
                    }
                    formik={formik}
                  />
                  {showUploadPopup?.showUploadPanImage && (
                    <UploadPopup
                      heading={t(
                        "authorizedSignatoryDetails.fields.panNumberImage.popup.heading"
                      )}
                      totalImages={1}
                      closePopup={() => togglePopup("pan")}
                      handleImageValue={handleImageValue}
                      uploadedImage={formik?.values?.panNumberImage}
                      fieldName={"panNumberImage"}
                      handleUpdateImageData={handleUpdateImageData}
                      handlePhysicalImage={handlePhysicalImage}
                      selectedOptionValue={formik?.values?.isPhysicalPan}
                      dynamicSize={2}
                      hidePhysicalCopyOption={true}
                      showDeleteIcon={
                        formik?.values?.isPanVerified
                          ? false
                          : !isPanOcrDone
                      }
                      showPreview={isPanOcrDone ? "show" : ""}
                    />
                  )}
                </Grid>
              )}

              {/* Name as per Aadhaar */}
              {!formik?.values?.isAadhaarSkipped && (
                <Grid item xs={12} sm={6}>
                  <TextFieldInput
                    field={{
                      name: "nameAsPerAadhaar",
                      label: t(
                        "authorizedSignatoryDetails.fields.nameAsPerAadhaar.label"
                      ),
                      required: true,

                        readonly: Boolean(
                          personalDetails?.isAadhaarVerified && formik.values?.nameAsPerAadhaar
                        ),
                      showCheckIcon: Boolean(personalDetails?.isAadhaarVerified && formik.values?.nameAsPerAadhaar),
                      placeholder: t(
                        "authorizedSignatoryDetails.fields.nameAsPerAadhaar.placeholder"
                      ),
                      onClear: () => {
                        const trimmedValue =
                          formik?.values?.nameAsPerAadhaar?.trim();
                        if (trimmedValue) {
                          formik.setFieldValue("nameAsPerAadhaar", "");
                        }
                      },
                      handleOnChange: handleAddharNameChange,
                    }}
                    formik={formik}
                  />
                </Grid>
              )}

              {/* Name as per PAN */}
              {!formik?.values?.isPanSkipped && (
                <Grid item xs={12} sm={6}>
                  <TextFieldInput
                    field={{
                      name: "nameAsPerPan",
                      label: t(
                        "authorizedSignatoryDetails.fields.nameAsPerPan.label"
                      ),
                        required: true,

                        readonly: Boolean(personalDetails?.isPanVerified && formik.values?.nameAsPerPan),
                        showCheckIcon: Boolean(personalDetails?.isPanVerified && formik.values?.nameAsPerPan),
                        placeholder: t(
                          "authorizedSignatoryDetails.fields.nameAsPerPan.placeholder"
                        ),
                      onClear: () => {
                        const trimmedValue =
                          formik?.values?.nameAsPerPan?.trim();
                        if (trimmedValue) {
                          formik.setFieldValue("nameAsPerPan", "");
                        }
                      },
                      handleOnChange: handlePanNameChange,
                    }}
                    formik={formik}
                  />
                </Grid>
              )}

              {/* Address Proof */}
              {formik?.values?.addressProofType && (
                <Grid item xs={12} sm={6}>
                  <SelectInput
                    field={{
                      label: t(
                        "authorizedSignatoryDetails.fields.addressProofImage.typeLabel"
                      ),
                      name: "addressProofType",
                      readonly: false,
                      options: ADDRESS_PROOF_OPTIONS,
                      placeholder: "Select Address Proof",
                    }}
                    formik={formik}
                  />
                </Grid>
              )}
              {formik?.values?.addressProofType && (
                <Grid item xs={12} sm={6}>
                  <UploadInput
                    field={{
                      name: "addressProofImage",
                      label: t(
                        "authorizedSignatoryDetails.fields.addressProofImage.imageLabel"
                      ),
                      placeholder: formik?.values?.isPhysicalAddressProof
                        ? t(
                            "authorizedSignatoryDetails.fields.addressProofImage.upload.physicalCopy"
                          )
                        : t(
                            "authorizedSignatoryDetails.fields.addressProofImage.upload.placeholder"
                          ),
                      placeholderUpload: t("commonPlaceholders.uploaded"),
                      required: true,
                      handleOnClick: () => togglePopup("addressProof"),
                      physicalCopyValue: formik?.values?.isPhysicalAddressProof,
                    }}
                    formik={formik}
                  />
                  {showUploadPopup?.showUploadAddressProofImage && (
                    <UploadPopup
                      heading={t(
                        "authorizedSignatoryDetails.fields.addressProofImage.popup.heading"
                      )}
                      totalImages={1}
                      closePopup={() => togglePopup("addressProof")}
                      handleImageValue={handleImageValue}
                      uploadedImage={formik?.values?.addressProofImage}
                      fieldName={"addressProofImage"}
                      handleUpdateImageData={handleUpdateImageData}
                      handlePhysicalImage={handlePhysicalImage}
                      selectedOptionValue={
                        formik?.values?.isPhysicalAddressProof
                      }
                      dynamicSize={2}
                    />
                  )}
                </Grid>
              )}

              {/* Primary Document */}
              {!(
                formik?.values?.isAadhaarSkipped || formik?.values?.isPanSkipped
              ) && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#1A407D",
                      }}
                    >
                      {t(
                        "authorizedSignatoryDetails.fields.primaryDocument.label"
                      )}
                    </Typography>
                    <Tooltip
                      title={t(
                        "authorizedSignatoryDetails.fields.primaryDocument.tooltip"
                      )}
                      placement="right"
                    >
                      <IconButton size="small" sx={{ p: 0, color: "#1A407D" }}>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <RadioGroup
                    row
                    name="primaryDocument"
                    value={formik.values.primaryDocument}
                    onChange={(e) =>
                      formik.setFieldValue("primaryDocument", e.target.value)
                    }
                  >
                    <FormControlLabel
                      value={PRIMARYDOCUMENT_OPTIONS?.Aadhaar}
                      control={<Radio />}
                      label={t(
                        "authorizedSignatoryDetails.fields.primaryDocument.options.aadhaar"
                      )}
                    />
                    <FormControlLabel
                      value={PRIMARYDOCUMENT_OPTIONS?.Pan}
                      control={<Radio />}
                      label={t(
                        "authorizedSignatoryDetails.fields.primaryDocument.options.pan"
                      )}
                    />
                  </RadioGroup>
                </Grid>
              )}

              {/* Salutation / First Name */}
              <Grid item xs={12} sm={6}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={5} md={4} lg={4} xl={4}>
                    <div className="salutationWidth">
                      <SelectInput
                        field={{
                          label: t(
                            "authorizedSignatoryDetails.fields.salutation.label"
                          ),
                          name: "salutation",
                          readonly: false,
                          options: masterData?.Salutation,
                          placeholder: t(
                            "authorizedSignatoryDetails.fields.salutation.placeholder"
                          ),
                          required: true,
                        }}
                        formik={formik}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={7} md={8} lg={8} xl={8}>
                    <TextFieldInput
                      field={{
                        name: "firstName",
                        label: t(
                          "authorizedSignatoryDetails.fields.firstName.label"
                        ),
                        required: true,
                        placeholder: t(
                          "authorizedSignatoryDetails.fields.firstName.placeholder"
                        ),
                        handleOnChange: (e) => {
                          const newValue = e.target.value?.replace(
                            /[^a-zA-Z\s]/g,
                            ""
                          );
                          formik.setFieldValue("firstName", newValue);
                        },
                        //readonly: hasAadhaarName || hasPanName,
                        readonly: !Boolean(
                          formik.values?.isAadhaarSkipped &&
                          formik.values?.isPanSkipped
                        )
                      }}
                      formik={formik}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} sm={6}>
                <TextFieldInput
                  field={{
                    name: "lastName",
                    label: t(
                      "authorizedSignatoryDetails.fields.lastName.label"
                    ),
                    required: false,
                    placeholder: t(
                      "authorizedSignatoryDetails.fields.lastName.placeholder"
                    ),
                    handleOnChange: (e) => {
                      const newValue = e.target.value?.replace(
                        /[^a-zA-Z\s]/g,
                        ""
                      );
                      formik.setFieldValue("lastName", newValue);
                    },
                    //readonly: hasAadhaarName || hasPanName,
                    readonly: !Boolean(
                      formik.values?.isAadhaarSkipped &&
                      formik.values?.isPanSkipped
                    ),
                  }}
                  formik={formik}
                />
              </Grid>

              {/* Contact Number */}
              <Grid item xs={12} sm={6}>
                <RHFPhoneInput
                  name="contactNumber"
                  countryCodeName="countryCode"
                  label={t(
                    "authorizedSignatoryDetails.fields.contactNumber.label"
                  )}
                  country={formik?.values?.countryCode ?? "IN"}
                  formik={formik}
                  placeholder={t(
                    "authorizedSignatoryDetails.fields.contactNumber.placeholder"
                  )}
                  required
                  showDialCode={true}
                />
              </Grid>

              {/* Email Address */}
              <Grid item xs={12} sm={6}>
                <TextFieldInput
                  field={{
                    name: "emailAddress",
                    label: t(
                      "authorizedSignatoryDetails.fields.emailAddress.label"
                    ),
                    placeholder: t(
                      "authorizedSignatoryDetails.fields.emailAddress.placeholder"
                    ),
                    required: true,
                  }}
                  formik={formik}
                />
              </Grid>

              {/* Profile Photo */}
              <Grid item xs={12} sm={6}>
                <UploadInput
                  field={{
                    name: "image",
                    label: t("commonLabels.passportSizePhoto"),
                    placeholder: formik?.values?.isPhysicalImage
                      ? t(
                          "authorizedSignatoryDetails.fields.image.upload.physicalCopy"
                        )
                      : t(
                          "authorizedSignatoryDetails.fields.image.upload.placeholder"
                        ),
                    placeholderUpload: t("commonPlaceholders.uploaded"),
                    required: true,
                    handleOnClick: () => togglePopup("profile"),
                    physicalCopyValue: formik?.values?.isPhysicalImage,
                  }}
                  formik={formik}
                />
                {showUploadPopup?.showUploadProfileImage && (
                  <UploadPopup
                    heading={t(
                      "authorizedSignatoryDetails.fields.image.popup.heading"
                    )}
                    totalImages={1}
                    closePopup={() => togglePopup("profile")}
                    handleImageValue={handleImageValue}
                    uploadedImage={formik?.values?.image}
                    fieldName={"image"}
                    handleUpdateImageData={handleUpdateImageData}
                    handlePhysicalImage={handlePhysicalImage}
                    selectedOptionValue={formik?.values?.isPhysicalImage}
                    dynamicSize={2}
                  />
                )}
              </Grid>
              <AddAuthorizedSignatoryButton
                title={t(
                  "authorizedSignatoryDetails.buttons.addAuthorizedSignatory"
                )}
                applicantNo={applicantNo}
                applicantTotal={applicantTotal}
                handleAddAuthorizedSignatory={handleAddAuthorizedSignatory}
              />
            </Grid>
          </form>
          <SkipApplicant />
          <CommonButton
            handlePrevious={handlePrevious}
            handleNext={() => {
              handleSubmit(false, false);
            }}
            saveDraftPath={route.UPDATE_SIGNATORY}
            saveDraftData={buildPayload(true)}
            saveAndPreview={() => {
              handleSubmit(false, true);
            }}
          />
        </>
      )}
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
}

export default memo(AuthorizedSignatoryDetails);
